import { useClientContext } from '@unifyapps/carbon/context/client/ClientContext';
import { ClientType } from '@unifyapps/carbon/context/client/types';
import { ColorScheme } from '@unifyapps/defs/types/interface';
import { SYSTEM_COLOR_SCHEME_COOKIE } from '@unifyapps/network/auth/const';
import { useEffect } from 'react';
import { useSystemColorScheme } from '../useSystemColorScheme';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year — must outlive any user session
const COOKIE_PATH = '/';

/**
 * Syncs resolved system color scheme (light/dark) to a cookie so the server can read it
 * when user preference is 'system'. Enables server-side compute context to use
 * theme for conditional logic without client-only APIs.
 *
 * No-ops in embedded clients (delta-matrix, web-sdk, etc.) where there is no SSR
 * middleware to consume the cookie.
 */
export function useSyncSystemColorSchemeCookie(): void {
  const clientContext = useClientContext();
  const systemColorScheme = useSystemColorScheme();

  useEffect(() => {
    // skip in embedded/non-SSR clients — cookie has no server consumer there

    if (typeof document === 'undefined') return;

    if (clientContext?.client !== ClientType.Matrix) return;
    const value = systemColorScheme === 'dark' ? ColorScheme.DARK : ColorScheme.LIGHT;
    // biome-ignore lint/suspicious/noDocumentCookie: required to sync system color scheme for server-side compute context
    document.cookie = `${SYSTEM_COLOR_SCHEME_COOKIE}=${value};path=${COOKIE_PATH};max-age=${COOKIE_MAX_AGE};samesite=lax${
      location.protocol === 'https:' ? ';secure' : ''
    }`;
  }, [systemColorScheme, clientContext?.client]);
}
